import React, { useEffect, useState, useCallback } from 'react';
import { responseHero } from '../../../utils/axiosInterceptor';
import { formatCurrency } from '../utils/formatCurrency';
import { BMP_RobotProduction, GetWalletResponse } from '../../../models/bmp.model';
import { getBMPWalletState } from '../../../api/getBMPWalletState';
import Decimal from 'decimal.js';
import { AddNewRobotProductionModal } from './modals/AddNewRobotProductionModal';
import { RobotToFixModal } from '../modals/RobotToFixModal';
import { formatCrypto } from '../../../utils/formatCrypto';

interface RobotWalletProps {
    onBack: () => void;
    onUpdate: () => void; // A globális walletState frissítéséhez
}

export const RobotWalletComponent: React.FC<RobotWalletProps> = ({ onBack, onUpdate }) => {
    const [productions, setProductions] = useState<BMP_RobotProduction[]>([]);
    const [walletData, setWalletData] = useState<GetWalletResponse | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false); // ADDEED: Transfer toggle trigger state

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [prodRes, walletRes] = await Promise.all([
                responseHero.get(`${process.env.REACT_APP_API_URL}/bmp/robot`),
                getBMPWalletState()
            ]);
            setProductions(prodRes.data);
            setWalletData(walletRes);
        } catch (err) {
            console.error("Hiba az adatok lekérésekor:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const robotBtcUsd = new Decimal(walletData?.holdingsUsd?.robotBtc || 0);
    const robotLiqUsd = new Decimal(walletData?.wallet?.robotLiquidity || 0);
    const totalValue = robotBtcUsd.plus(robotLiqUsd);

    const btcAmount = walletData?.marketPrices?.btc && !robotBtcUsd.isZero()
        ? robotBtcUsd.div(walletData.marketPrices.btc).toNumber()
        : 0;

    return (
        <div className="w-full max-w-6xl mx-auto p-6 animate-in fade-in duration-700">
            <div className="flex justify-between items-center mb-8">
                <button onClick={onBack} className="text-slate-500 hover:text-slate-800 font-bold flex items-center gap-2 transition-colors group">
                    <span className="group-hover:-translate-x-1 transition-transform">←</span> Vissza a portfólióhoz
                </button>

                {/* ADDEED: New Action Button */}
                <button 
                    onClick={() => setIsModalOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg shadow-blue-600/20 transition-all active:scale-95"
                >
                    Új termelés rögzítése
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                <div className="lg:col-span-2 bg-slate-900 rounded-[2.5rem] p-10 text-white shadow-2xl relative overflow-hidden flex flex-col justify-center min-h-[220px]">
                    <div className="relative z-10">
                        <h2 className="text-blue-400 text-[10px] font-black uppercase tracking-[0.3em] mb-3">Robot Portfolio Összérték</h2>
                        <div className="text-6xl font-black tracking-tighter">
                            {formatCurrency(totalValue.toNumber())}
                        </div>
                        <p className="mt-4 text-slate-400 text-xs font-medium max-w-md">
                            A robot által termelt eszközök piaci értéke és a fel nem használt likviditás összege.
                        </p>
                    </div>
                    <div className="absolute right-[-20px] bottom-[-40px] opacity-[0.03] text-[15rem] font-black italic select-none">AI</div>
                </div>

                <div className="bg-white border-2 border-slate-100 rounded-[2.5rem] p-8 flex flex-col justify-center shadow-sm">
                    <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2">Robot Likviditás (USD)</span>
                    <div className="text-3xl font-black text-slate-800">
                        {formatCurrency(robotLiqUsd.toNumber())}
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-blue-500 font-bold text-xs">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        AI Tartalék
                    </div>
                </div>
            </div>

            {/* UPDATED: Structured full-width container dividing values on the left and actions on the right */}
            <div className="grid grid-cols-1 gap-6 mb-12">
                <div className="bg-blue-50/50 border border-blue-100 rounded-3xl p-8 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center group hover:bg-blue-50 transition-all hover:shadow-md">
                    <div>
                        <p className="text-blue-600 text-[10px] font-black uppercase tracking-widest mb-1">Összes termelt Bitcoin</p>
                        <div className="text-3xl font-black text-slate-900 mb-1">
                            {formatCurrency(robotBtcUsd.toNumber())}
                        </div>
                        <p className="text-sm font-bold text-slate-500">
                            {formatCrypto(btcAmount)} <span className="text-[10px] opacity-60">BTC</span>
                        </p>
                    </div>
                    
                    {/* ADDEED: Contextual Migration Trigger Interface Action Block */}
                    <div className="flex items-center gap-4 w-full sm:w-auto self-end sm:self-center">
                        <button 
                            onClick={() => setIsTransferModalOpen(true)}
                            disabled={btcAmount <= 0}
                            className="w-full sm:w-auto px-5 py-3 rounded-xl font-bold uppercase text-[11px] tracking-wide bg-white border border-blue-200 text-blue-700 hover:bg-blue-600 hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-blue-700 shadow-sm transition-all active:scale-98"
                        >
                            Transzferálás a Fix zsebbe
                        </button>
                        <div className="hidden sm:flex w-14 h-14 bg-white rounded-2xl items-center justify-center shadow-sm text-xl group-hover:scale-110 transition-transform">🤖</div>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-sm overflow-hidden mb-10">
                <div className="px-10 py-8 border-b border-slate-50 flex justify-between items-center">
                    <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Termelési Előzmények</h3>
                    <div className="px-3 py-1 bg-slate-100 rounded-full text-[9px] font-bold text-slate-500 uppercase">{productions.length} tétel</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50">
                                <th className="px-10 py-5">Dátum</th>
                                <th className="px-6 py-5">Eszköz</th>
                                <th className="px-6 py-5 text-right">Mennyiség</th>
                                <th className="px-6 py-5">Megjegyzés</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {productions.map((prod) => (
                                <tr key={prod.id} className="hover:bg-slate-50/30 transition-colors group">
                                    <td className="px-10 py-5 text-sm font-medium text-slate-500">
                                        {new Date(prod.date).toLocaleDateString('hu-HU')}
                                    </td>
                                    <td className="px-6 py-5 font-bold text-slate-700">
                                        {prod.assetType === 1 ? 'BTC' : prod.assetType === 2 ? 'ETH' : 'Egyéb'}
                                    </td>
                                    <td className="px-6 py-5 text-right font-mono text-sm font-bold text-blue-600">
                                        {new Decimal(prod.producedQuantity || 0).toString()}
                                    </td>
                                    <td className="px-6 py-5 text-xs text-slate-400 italic max-w-xs truncate">
                                        {prod.note || '-'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddNewRobotProductionModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSuccess={() => { setIsModalOpen(false); fetchData(); onUpdate(); }}
            />

            {/* ADDEED: Component Insertion Block */}
            <RobotToFixModal 
                isOpen={isTransferModalOpen}
                onClose={() => setIsTransferModalOpen(false)}
                btcAmount={btcAmount}
                onSuccess={() => {
                    setIsTransferModalOpen(false);
                    fetchData();
                    onUpdate();
                }}
            />

        </div>
    );
};